import { Alert, ErrorIcon, Input, SearchIcon, Spinner, Text, Toggle } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { useOrganizationFeatureFlagsStore } from '../../featureFlags/store'

export const OrganizationFeatureFlagsTab = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const toast = useToast()
	const [filterText, setFilterText] = useState('')

	const { flags, isLoading, isMutating, isForbidden, fetchFlags, toggleFlag } = useOrganizationFeatureFlagsStore()

	useEffect(() => {
		if (organizationId) {
			fetchFlags(organizationId)
		}
	}, [organizationId, fetchFlags])

	const handleToggle = async (flagKey: string, enabled: boolean) => {
		if (!organizationId) return

		try {
			await toggleFlag(organizationId, flagKey, enabled)
			toast.showToast('Feature flag updated')
		} catch (error) {
			toast.showToast(error instanceof Error ? error.message : 'Failed to update feature flag', 'error')
		}
	}

	const filteredFlags = useMemo(() => {
		if (!filterText.trim()) {
			return flags
		}
		const searchLower = filterText.toLowerCase()
		return flags.filter(
			(flag) =>
				flag.name.toLowerCase().includes(searchLower) ||
				flag.key.toLowerCase().includes(searchLower) ||
				flag.description?.toLowerCase().includes(searchLower),
		)
	}, [flags, filterText])

	if (isLoading) {
		return (
			<div className='p-6'>
				<Spinner />
			</div>
		)
	}

	if (isForbidden) {
		return (
			<div className='p-6'>
				<Alert
					message='Only engineering team members can access feature flags. If you believe this is an error, please contact your administrator.'
					icon={ErrorIcon}
					className='bg-error-100 border-error-300'
				/>
			</div>
		)
	}

	return (
		<div className='p-6'>
			<Text variant='h3' className='mb-6'>
				Feature Flags
			</Text>
			<Text variant='small' className='text-neutral-600 mb-6'>
				Toggle feature flags on or off for this organization. Changes take effect immediately.
			</Text>

			<div className='mb-4 relative'>
				<div className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10'>
					<SearchIcon size={16} className='fill-neutral-500' />
				</div>
				<Input
					placeholder='Filter feature flags by name, key, or description...'
					value={filterText}
					onChange={(e) => setFilterText(e.target.value)}
					className='pl-9'
				/>
			</div>

			<div className='space-y-4'>
				{filteredFlags.map((flag) => (
					<div
						key={flag.id}
						className={`flex items-center justify-between p-6 border border-neutral-200 rounded-lg transition-colors ${
							flag.enabled ? 'bg-primary-50 hover:bg-primary-100' : 'bg-neutral-100 hover:bg-neutral-200'
						}`}>
						<div className='flex-1'>
							<Text
								variant='medium'
								weight='semibold'
								className={flag.enabled ? 'text-primary-800' : 'text-neutral-700'}>
								{flag.name}
							</Text>
							<Text variant='small' className='text-neutral-600 mt-1'>
								{flag.key}
							</Text>
							{flag.description && (
								<Text variant='small' className='text-neutral-500 mt-1'>
									{flag.description}
								</Text>
							)}
							<Text variant='xsmall' className='text-neutral-400 mt-2'>
								Default: {flag.enabledByDefault ? 'Enabled' : 'Disabled'}
							</Text>
						</div>
						<Toggle
							checked={flag.enabled}
							onCheckedChange={(enabled: boolean) => handleToggle(flag.key, enabled)}
							label={flag.enabled ? 'Enabled' : 'Disabled'}
							disabled={isMutating}
						/>
					</div>
				))}

				{filteredFlags.length === 0 && flags.length > 0 && (
					<div className='p-8 text-center border border-neutral-200 bg-neutral-0 rounded-lg'>
						<Text variant='medium' className='text-neutral-500'>
							No feature flags match your filter
						</Text>
						<Text variant='small' className='text-neutral-400 mt-2'>
							Try adjusting your search criteria.
						</Text>
					</div>
				)}

				{flags.length === 0 && (
					<div className='p-8 text-center border border-neutral-200 bg-neutral-0 rounded-lg'>
						<Text variant='medium' className='text-neutral-500'>
							No feature flags available
						</Text>
						<Text variant='small' className='text-neutral-400 mt-2'>
							Create feature flags in the Feature Flags section to manage them here.
						</Text>
					</div>
				)}
			</div>
		</div>
	)
}
